import React, { useState } from "react";
import { Card, Alert, Button, Badge, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faPlay, 
    faRefresh, 
    faExclamationTriangle, 
    faInfoCircle,
    faCheckCircle
} from "@fortawesome/free-solid-svg-icons";
import useGetEventDetailsApi from "../../hooks/useGetEventDetails";
import useGetResources from "../../hooks/useGetResources";
import KeepAliveInstance from "./KeepAliveInstance";

/**
 * KeepAlive Component
 * 
 * This is the main Keep Alive tab component that displays instances and allows
 * users to keep them alive using AWS Step Functions.
 */
const KeepAlive = ({ eventId, environment, theme }) => {
    // State management - load from localStorage on mount
    const [keepAliveStatus, setKeepAliveStatus] = useState(() => {
        const saved = localStorage.getItem(`keepAliveStatus_${eventId}_${environment}`);
        return saved ? JSON.parse(saved) : {};
    });
    
    // API hooks
    const { eventDetails, error: eventError, isLoaded: eventLoaded } = useGetEventDetailsApi(eventId, environment);
    const { 
        resources: instances, 
        error: instancesError, 
        isLoaded: instancesLoaded, 
        refetchResources 
    } = useGetResources(eventId, environment, "get_event_instances");

    // Event handlers
    const handleKeepAliveComplete = (instanceId, success, message) => {
        const newStatus = {
            ...keepAliveStatus,
            [instanceId]: {
                status: success ? 'completed' : 'failed',
                message: message,
                timestamp: new Date().toLocaleTimeString(),
                alreadyExists: message && message.includes("already done")
            }
        };
        
        setKeepAliveStatus(newStatus);
        // Save to localStorage for persistence
        localStorage.setItem(`keepAliveStatus_${eventId}_${environment}`, JSON.stringify(newStatus));
    };


    // Loading state
    if (!instancesLoaded || !eventLoaded) {
        return (
            <div className="text-center p-4">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading instances...</span>
                </Spinner>
            </div>
        );
    }

    // Error state
    if (instancesError && eventError) {
        return (
            <Alert variant="danger">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                Error loading data: {instancesError || eventError}
            </Alert>
        );
    }

    return (
        <div className="keep-alive-container">
            <Card className={`${theme}-theme`}>
                <Card.Header className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                <FontAwesomeIcon icon={faPlay} className="me-2 text-primary" />
                                Keep Instances Alive
                            </h5>
                    <Button 
                        variant="outline-primary" 
                        size="sm" 
                        onClick={refetchResources} 
                        disabled={!instancesLoaded}
                    >
                        <FontAwesomeIcon icon={faRefresh} className="me-1" />
                        Refresh Instances
                    </Button>
                </Card.Header>
                <Card.Body>
                    {/* Event details error */}
                    {eventError && (
                        <Alert variant="info" className="mb-3">
                            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                            <strong>Note:</strong> Unable to load event details.
                            <br />
                            <small>Error: {eventError}</small>
                        </Alert>
                    )}

                    {/* Instances error */}
                    {instancesError && (
                        <Alert variant="warning" className="mb-3">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                            <strong>Warning:</strong> Unable to load instances for this event.
                            <br />
                            <small>Error: {instancesError}</small>
                        </Alert>
                    )}

                    {/* Instances list */}
                    {instances && instances.length > 0 ? (
                        <div className="instances-list">
                            {instances.map((instance, index) => {
                                const keepAliveInfo = keepAliveStatus[instance.instanceId];
                                const isKeepAliveCompleted = keepAliveInfo?.status === 'completed';
                                const isKeepAliveFailed = keepAliveInfo?.status === 'failed';

                                return (
                                    <div key={index} className="instance-item mb-3 p-3 border rounded">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div className="instance-info">
                                                <div className="d-flex align-items-center mb-2">
                                                    <strong className="me-2">Instance ID:</strong>
                                                    <code className="me-3">{instance.instanceId}</code>
                                                    <Badge 
                                                        bg={instance.state === 'running' ? 'success' : 'warning'} 
                                                        className="me-2"
                                                    >
                                                        {instance.state}
                                                    </Badge>
                                                    <small className="text-muted">
                                                        {instance.instanceType}
                                                    </small>
                                                </div>
                                                
                                                {keepAliveInfo && (
                                                    <div className="keep-alive-status">
                                                        {isKeepAliveCompleted && (
                                                            <Badge bg="success" className="me-2">
                                                                <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                                                Keep Alive Done
                                                            </Badge>
                                                        )}
                                                        {isKeepAliveFailed && (
                                                            <Badge bg="danger" className="me-2">
                                                                <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                                                                Keep Alive Failed
                                                            </Badge>
                                                        )}
                                                        <small className="text-muted">
                                                            {keepAliveInfo.timestamp} - {keepAliveInfo.message}
                                                        </small>
                                                    </div>
                                                )}
                                            </div>
                                            
                                            <div className="instance-actions">
                                                <KeepAliveInstance
                                                    instanceId={instance.instanceId}
                                                    environment={environment}
                                                    eventId={eventId}
                                                    onKeepAliveComplete={handleKeepAliveComplete}
                                                    isCompleted={isKeepAliveCompleted}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <Alert variant="info" className="mt-3">
                            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                            <strong>No instances found for this event.</strong>
                            <br />
                            <small>
                                This could mean:
                                <ul className="mt-2 mb-0">
                                    <li>The event is still starting up and instances haven't been created yet</li>
                                    <li>The instances don't have the event ID in their Name tag</li>
                                    <li>The event has finished and instances have been terminated</li>
                                </ul>
                            </small>
                        </Alert>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default KeepAlive;
